import clsx from 'clsx';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountChart } from './AccountChart';
import { AmountTable, EditableAmount } from '../../UI/components/Table';
import {
  Account,
  AmountListItem,
  Asset,
  CreateFile,
  darkModeColors,
  decayedLimit,
  DecayedLimitType,
  WithInlineDeleteAction,
  displayAccountType,
  displayMonetaryAmount,
  EditAccountValueModal,
  ElementsButton,
  fadeSlideIn,
  FileListItem,
  getIsPlaidLinkError,
  getOwnershipDisplay,
  getPlaidLinkID,
  getTotalOwnershipPercentage,
  Icon,
  isDecayed,
  LinkedAccountTooltip,
  MappedAccountStatus,
  MonetaryAmount,
  MonetaryAmountDataPoint,
  SideOverBanner,
  SideOverHeader,
  useClientContext,
  useDeleteDataPoint,
  useFeatureFlagsContext,
  useFileAttachmentsForItem,
  SMTailwindMinPixelBreakpointWidth,
} from '../..';
import { PlaidConnectionErrorBanner } from '../../UI/components/Plaid/PlaidConnectionErrorBanner';
import EditAccountDetails from './EditAccountDetails';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useMediaQuery } from '@mui/material';
import { offset } from '@floating-ui/react';

export interface AccountDetailsIndexPageProps {
  loaderData: {
    exitRoute: string;
    account: Account;
    householdID: string;
    firmClientID: string;
    accountID: string;
    graphFilter: any;
  };
  account: Account;
  accountQueryKey: QueryKey;
  ownedValue: MonetaryAmount;
  valueDataPointsQueryKey: QueryKey;
  valueDataPoints: MonetaryAmountDataPoint[];
}

export const AccountDetailsIndexPage: React.FC<AccountDetailsIndexPageProps> = ({
  account,
  loaderData,
  ownedValue,
  valueDataPoints,
  accountQueryKey,
  valueDataPointsQueryKey,
}) => {
  const { exitRoute, graphFilter } = loaderData;

  // Access
  const queryClient = useQueryClient();
  const { clientID, householdID } = useClientContext();
  const { allowBAA, allowDocuments } = useFeatureFlagsContext();
  const { showClientEditAccountButton } = useFeatureFlagsContext();
  const { t: tLoan } = useTranslation('loan');
  const { t: tCommon } = useTranslation('common');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tRealEstate } = useTranslation('realEstate');
  const { t: tCashAccount } = useTranslation('cashAccount');
  const { t: tAccountSideover } = useTranslation('accountSideover');

  // Mutations
  const { deleteDataPoint } = useDeleteDataPoint();
  const { fileAttachments } = useFileAttachmentsForItem({ itemID: account.id, tenantID: householdID });

  // State
  const [isEditingValue, setIsEditingValue] = useState<boolean>(false);
  const [deleteActiveID, setDeleteActiveID] = useState<string | null>(null);

  // Derived
  const plaidLinkID = getPlaidLinkID(account);
  const isFullyOwned = getTotalOwnershipPercentage(account.ownership) === 1;
  const isClosed = !!account.closedAt;
  const latestDataPoint = valueDataPoints[0];
  const isDecayedAccount = isDecayed(
    latestDataPoint?.dateTime ?? latestDataPoint?.updatedAt,
    decayedLimit[account?.__typename as DecayedLimitType]
  );
  const isSmall = useMediaQuery(`(max-width: ${SMTailwindMinPixelBreakpointWidth}px)`);

  // Functions
  function handleDeletePastValueDataPoint(dataPoint: MonetaryAmountDataPoint) {
    deleteDataPoint.mutate(
      { id: dataPoint.id, tenantID: householdID },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(valueDataPointsQueryKey);
          queryClient.invalidateQueries(accountQueryKey);
        },
      }
    );
  }

  return (
    <AnimatePresence>
      <SideOverHeader className="ml-1 pt-[3px]" exitText={tCommon('close')} exit={exitRoute} />

      {showClientEditAccountButton && (
        <EditAccountDetails
          key="AccountDetails_editAccountDetails"
          account={account}
          clientID={clientID}
          householdID={householdID}
          isClosed={isClosed}
          onUpdateValue={() => setIsEditingValue(true)}
        />
      )}

      {getIsPlaidLinkError(account) && plaidLinkID && (
        <div className="mt-8">
          <PlaidConnectionErrorBanner householdID={householdID} plaidItemID={plaidLinkID} />
        </div>
      )}
      <motion.div key="AccountDetails_main_details" {...fadeSlideIn()}>
        {isClosed && <SideOverBanner variant="warning" icon="archive" label={tAccountSideover('closed-account-banner')} />}
        {!isFullyOwned && (
          <SideOverBanner
            icon="percentage"
            label={
              account.__typename === 'Loan'
                ? tCommon('loan-partial-ownership-banner')
                : tCommon('account-partial-ownership-banner')
            }
          />
        )}
        <div
          className="mt-8 text-base font-bold tracking-[0.29px] text-darkMode-gray-medium"
          aria-label={tCommon('account-type')}
        >
          {displayAccountType(account, tLoan, tCashAccount, tCommon, tInvestment, tRealEstate)}
        </div>
        <div
          className="mt-1 text-[1.75rem] font-extrabold text-white flex items-center space-x-2"
          aria-label={tCommon('account-nickname')}
        >
          <span className="inline">{account.name}</span>
          {(account.__typename === 'Investment' ||
            account.__typename === 'Loan' ||
            account.__typename === 'CashAccount') &&
            account.linkStatus &&
            ((jsx: JSX.Element) =>
              allowBAA ? (
                <LinkedAccountTooltip
                  placement="top-start"
                  offset={[-48, 4]}
                  linkStatus={account.linkStatus}
                  accountLinkProvider={account.accountLinkProvider}
                  linkedInstitutionName={account.linkedInstitutionName}
                >
                  {jsx}
                </LinkedAccountTooltip>
              ) : (
                jsx
              ))(
              <span className="inline-flex items-center">
                {account.linkStatus === MappedAccountStatus.Active ? (
                  <Icon icon="link" className="text-palette-500" />
                ) : (
                  <Icon icon="link" style={{ color: darkModeColors.danger }} />
                )}
              </span>
            )}
        </div>

        {isDecayedAccount && (
          <div className="mt-8 flex items-center">
            <Icon icon="warning" className="mr-1 inline-block h-6 w-6 text-darkMode-warning" />
            <span className="text-base font-normal leading-[19px] tracking-[.29px] text-darkMode-warning">
              {tAccountSideover('last-updated', {
                age: formatDistanceToNowStrict(new Date(latestDataPoint?.dateTime ?? latestDataPoint?.updatedAt)),
              })}
            </span>
          </div>
        )}
        <div className={clsx('flex flex-row items-center', !isDecayedAccount && 'mt-8')}>
          <div
            className={clsx('relative -left-3 flex flex-row rounded-lg px-2 align-middle')}
            style={{ width: 'calc(100% + 12px)' }}
          >
            <label htmlFor={'account-value'} className="tracking-[0.38px] dark:focus-within:text-palette-600">
              <div className="flex flex-row pt-[5px] text-5xl leading-12 tracking-[0.38px]">
                <EditableAmount
                  isOpenState={[isEditingValue, setIsEditingValue]}
                  isReadOnly={isClosed}
                  prefix={
                    <span
                      className={clsx(
                        'mt-1 text-darkMode-gray-light',
                        isDecayedAccount && 'text-darkMode-warning opacity-[.64]'
                      )}
                    >
                      $
                    </span>
                  }
                  textClassName="text-5xl font-bold pl-1"
                  amount={displayMonetaryAmount(ownedValue, { withoutDollarSign: true })}
                  editElement={({ setOpen }) => (
                    <EditAccountValueModal onClose={() => setOpen(false)} account={account} householdID={householdID} />
                  )}
                />
              </div>
            </label>
          </div>
        </div>
        <AccountChart
          accountID={account.id}
          householdID={householdID}
          defaultRangeFilter={graphFilter?.rangeFilter}
          groupID={account?.__typename === 'Loan' ? account.balance.id : (account as Asset).value.id}
          defaultChartRangeOption={graphFilter?.chartRangeOption}
        />
        {account.notes && (
          <>
            <div className="mt-8 text-sm text-darkMode-gray-light">{tCommon('notes')}</div>
            <div className="text-lg text-white" aria-label={tCommon('notes')}>
              {account.notes ?? ''}
            </div>
          </>
        )}
        <div className="mt-8 text-sm text-darkMode-gray-light">{tCommon('owned-by')}</div>
        <div className="text-lg text-white" aria-label={tCommon('account-owner')}>
          {getOwnershipDisplay(account.ownership)}
        </div>

        {allowDocuments && (
          <>
            {isClosed ? (
              fileAttachments.length > 0 && (
                <p className="mt-14 text-base font-bold text-darkMode-gray-medium">
                  {tAccountSideover('related-documents')}
                </p>
              )
            ) : (
              <CreateFile
                className="mt-14"
                itemID={account.id}
                tenantID={householdID}
                label={tAccountSideover('related-documents')}
              />
            )}

            <div className="mt-7">
              {fileAttachments.map((fileAttachment) => (
                <FileListItem
                  itemID={account.id}
                  tenantID={householdID}
                  file={fileAttachment.file}
                  key={fileAttachment.file.id}
                  fileAttachmentID={fileAttachment.id}
                  className="p-1 [&_.click-anchor-reference-element-trigger]:mr-[14px]"
                />
              ))}
            </div>
          </>
        )}
        <AmountTable className="mt-12" hideOnEmpty title={tAccountSideover('past-values-heading')}>
          {valueDataPoints.map((dataPoint) => (
            <WithInlineDeleteAction<HTMLDivElement>
              key={dataPoint.id}
              floatingMiddleware={[offset(8)]}
              deleteColor={darkModeColors.danger}
              currentPendingDeleteID={deleteActiveID}
              disabled={isClosed || valueDataPoints.length === 1}
              actionMode={isSmall ? 'click' : 'hover'}
              floatingPlacement={isSmall ? 'top' : 'right'}
              setCurrentPendingDeleteID={setDeleteActiveID}
              onDeleteAnimationComplete={() => handleDeletePastValueDataPoint(dataPoint)}
              deleteElement={
                isSmall ? (
                  <ElementsButton label="Delete" theme="danger" size="small" />
                ) : (
                  <Icon icon="removeCircle" className="text-darkMode-danger h-5 w-5" />
                )
              }
            >
              {({ className, motionProps, setReference, getFloatingReferenceProps }) => (
                <AmountListItem
                  motionProps={motionProps}
                  amount={dataPoint.data.value}
                  setFloatingReference={setReference}
                  getFloatingReferenceProps={getFloatingReferenceProps}
                  title={format(parseISO(dataPoint.dateTime), 'MMMM d, yyyy')}
                  className={clsx(className, 'min-h-12 px-4 py-3 first:rounded-t-2xl last:rounded-b-2xl')}
                />
              )}
            </WithInlineDeleteAction>
          ))}
        </AmountTable>
      </motion.div>
    </AnimatePresence>
  );
};
